/**
 * RubricAssessmentPanel — admin-visible rubric scorecard for a startup
 * or investor. Shows each criterion with score, tier, evidence, and
 * reasoning so the admin can audit how the assessment was reached.
 *
 * Same component for both entities; the criteria themselves come from
 * the backend (Company.rubric_assessment_json or
 * Investor.rubric_assessment_json) so adding a new criterion is a
 * one-place change in services/rubric/definitions.py.
 */
import React, { useEffect, useState } from 'react';
import {
  Loader2, Sparkles, ChevronDown, ChevronRight, Check, AlertTriangle,
  ListChecks, RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import ScoringMathBreakdown, { ScoreComponent } from './ScoringMathBreakdown';

export interface RubricCriterion {
  key: string;
  name: string;
  weight: number;
  score: number;
  tier: string;
  evidence: string[];
  reasoning: string;
  data_gaps: string[];
}

export interface RubricAssessment {
  criteria: RubricCriterion[];
  overall_score: number;
  overall_tier: string;
  headline: string;
  top_strengths: string[];
  top_weaknesses: string[];
  recommended_next_step: string;
  model_used?: string;
  generated_at?: string;
  rubric_version?: string;
}

interface Props {
  entityKind: 'company' | 'investor';
  entityId: number;
}

const tierColor = (tier: string): string => {
  switch (tier) {
    case 'exceptional': return 'bg-green-100 text-green-800 border-green-200';
    case 'strong': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'adequate': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'weak': return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'critical_gap': return 'bg-red-50 text-red-700 border-red-200';
    default: return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

const scoreBgColor = (s: number): string => {
  if (s >= 80) return 'bg-green-500';
  if (s >= 60) return 'bg-emerald-500';
  if (s >= 40) return 'bg-yellow-500';
  if (s >= 20) return 'bg-orange-500';
  return 'bg-red-500';
};


const CriterionRow: React.FC<{ c: RubricCriterion }> = ({ c }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 text-left"
      >
        <div className="flex-shrink-0">
          {open
            ? <ChevronDown className="w-4 h-4 text-gray-400" />
            : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </div>
        <div className="flex-shrink-0 w-12 text-right">
          <span className={`text-lg font-semibold ${c.score >= 60 ? 'text-emerald-700' : c.score >= 40 ? 'text-yellow-700' : 'text-red-700'}`}>
            {c.score}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{c.name}</span>
            <span className={`px-1.5 py-0.5 text-[10px] font-medium border rounded ${tierColor(c.tier)}`}>
              {c.tier.replace(/_/g, ' ')}
            </span>
            <span className="text-[10px] text-gray-400">weight {(c.weight * 100).toFixed(0)}%</span>
          </div>
        </div>
        <div className="hidden sm:block flex-shrink-0 w-32">
          <div className="w-full h-1.5 bg-gray-100 rounded">
            <div
              className={`h-1.5 rounded ${scoreBgColor(c.score)}`}
              style={{ width: `${Math.max(0, Math.min(100, c.score))}%` }}
            />
          </div>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 space-y-2 border-t border-gray-100">
          {c.reasoning && (
            <div className="text-sm text-gray-700 leading-snug">{c.reasoning}</div>
          )}
          {c.evidence.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                Evidence
              </div>
              <ul className="space-y-0.5">
                {c.evidence.map((e, i) => (
                  <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5">
                    <Check className="w-3 h-3 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {c.data_gaps.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                Data we wish we had
              </div>
              <ul className="space-y-0.5">
                {c.data_gaps.map((g, i) => (
                  <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                    <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const RubricAssessmentPanel: React.FC<Props> = ({ entityKind, entityId }) => {
  const [assessment, setAssessment] = useState<RubricAssessment | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const basePath = entityKind === 'company'
    ? `/companies/${entityId}/rubric`
    : `/investors/${entityId}/rubric`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(basePath);
      setAssessment(data?.assessment ?? null);
      setGeneratedAt(data?.generated_at ?? null);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load rubric');
    } finally {
      setLoading(false);
    }
  };

  // Auto-score: when the panel opens and the rubric is missing, run
  // the synchronous scorer (no YouTube/LinkedIn refresh) so the user
  // sees a populated rubric instead of a "Score now" button. The
  // background-queue path is fragile in production (Railway worker
  // recycle, max_tokens truncation) — calling sync from the UI gives
  // us deterministic feedback and a clear error if it really fails.
  const triedAutoRef = React.useRef(false);

  const runScoreSync = async (toastOnSuccess: boolean) => {
    setGenerating(true);
    try {
      const params =
        entityKind === 'investor' ? { refresh_external: false } : {};
      const { data } = await api.post(`${basePath}/sync`, null, { params });
      if (data && typeof data === 'object') {
        setAssessment(data as RubricAssessment);
        setGeneratedAt(
          (data as any).generated_at || new Date().toISOString(),
        );
        if (toastOnSuccess) toast.success('Rubric scored');
      } else {
        toast.error('Rubric scoring returned no data');
      }
    } catch (e: any) {
      toast.error(
        e?.response?.data?.detail ||
          e?.response?.data?.message ||
          'Rubric scoring failed — check server logs',
      );
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => { load(); }, [basePath]);

  useEffect(() => {
    if (loading) return;
    if (assessment) return;
    if (generating) return;
    if (triedAutoRef.current) return;
    triedAutoRef.current = true;
    runScoreSync(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, assessment]);

  const handleGenerate = async () => {
    // Manual button — uses the sync endpoint so the user sees the
    // result immediately. For investors, refresh_external=true to
    // pull fresh YouTube + LinkedIn signals (slower, ~60s) since the
    // user explicitly asked to re-score.
    setGenerating(true);
    try {
      const params =
        entityKind === 'investor' ? { refresh_external: true } : {};
      const { data } = await api.post(`${basePath}/sync`, null, { params });
      if (data && typeof data === 'object') {
        setAssessment(data as RubricAssessment);
        setGeneratedAt(
          (data as any).generated_at || new Date().toISOString(),
        );
        toast.success('Rubric scored');
      } else {
        toast.error('Rubric scoring returned no data');
      }
    } catch (e: any) {
      toast.error(
        e?.response?.data?.detail ||
          e?.response?.data?.message ||
          'Rubric scoring failed — check server logs',
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-indigo-500" />
            {entityKind === 'company' ? 'Startup' : 'Investor'} rubric
          </h2>
          {assessment ? (
            <p className="text-sm text-gray-700 mt-1 leading-snug">
              {assessment.headline}
            </p>
          ) : (
            <p className="text-sm text-gray-600 mt-1 leading-relaxed">
              {entityKind === 'company'
                ? '8-criterion assessment scoring team, product, market, traction, financials, capital readiness, competition, and narrative. Visible to admin with evidence per criterion.'
                : '8-criterion assessment scoring thesis clarity, fit signals, decisiveness, value-add, reputation, current direction (always pulls fresh YouTube + LinkedIn), engagement, and reliability.'}
            </p>
          )}
          {generatedAt && (
            <p className="text-[10px] text-gray-400 mt-0.5">
              Generated {new Date(generatedAt).toLocaleString()}
              {assessment?.model_used && ` · model: ${assessment.model_used}`}
              {assessment?.rubric_version && ` · ${assessment.rubric_version}`}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {assessment && (
            <div className={`px-2 py-1 text-xs font-semibold border rounded-md ${tierColor(assessment.overall_tier)}`}>
              <span className="text-base mr-1">{assessment.overall_score}</span>
              <span className="text-[10px] uppercase">{assessment.overall_tier.replace(/_/g, ' ')}</span>
            </div>
          )}
          <button
            onClick={handleGenerate}
            disabled={generating || loading}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
          >
            {generating
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : assessment
                ? <RefreshCw className="w-3 h-3" />
                : <Sparkles className="w-3 h-3" />}
            {assessment ? 'Re-score' : 'Score now'}
          </button>
        </div>
      </div>

      {loading && !assessment && (
        <div className="flex items-center gap-2 text-gray-500 py-3 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading rubric…
        </div>
      )}

      {assessment && (
        <>
          {/* Criterion list */}
          <div className="space-y-1.5">
            {assessment.criteria.map((c) => (
              <CriterionRow key={c.key} c={c} />
            ))}
          </div>

          {/* Strengths / weaknesses / next step */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
            {assessment.top_strengths.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">
                  Top strengths
                </h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  {assessment.top_strengths.map((s, i) => (
                    <li key={i} className="leading-snug">• {s}</li>
                  ))}
                </ul>
              </div>
            )}
            {assessment.top_weaknesses.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">
                  Top weaknesses
                </h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  {assessment.top_weaknesses.map((w, i) => (
                    <li key={i} className="leading-snug">• {w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {assessment.recommended_next_step && (
            <div className="bg-indigo-50 text-indigo-900 text-sm p-3 rounded leading-snug">
              <strong>Next step:</strong> {assessment.recommended_next_step}
            </div>
          )}

          {/* Math transparency: show how the per-criterion scores
              roll up to the overall number, where the upside lives,
              and what's missing. The user explicitly asked for this:
              "we want to see how we are using [research] mathematically
              to create a numerical value... so we can see areas for
              improvement, areas left out." */}
          <ScoringMathBreakdown
            title="How we computed the overall score"
            overallScore={assessment.overall_score}
            components={assessment.criteria.map<ScoreComponent>((c) => ({
              key: c.key,
              label: c.name,
              score: c.score,
              weight: c.weight,
              evidence: c.evidence,
              data_gaps: c.data_gaps,
            }))}
          />
        </>
      )}
    </section>
  );
};

export default RubricAssessmentPanel;
